/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList} from '@angular/core';

import {WizardStepComponent} from './wizard-step/wizard-step.component';

import {isMobileDevice} from '../../utils/browser';
import {isPresent} from '../../utils/lang';

/**
 * Simple wizard component, integrating form validation.
 *
 * Sample usage :
 *
 * ```
 *  <gp-wizard>
 *    <gp-wizard-step [form]="step1Form">
 *       <form #step1Form="ngForm">
 *           <input type="text" class="form-control" required
 *             [(ngModel)]="model.name" ngControl="name" #name="ngForm">
 *           <div [attr.data-hidden]="name.valid || name.pristine" class="alert alert-danger">
 *             Name is required
 *           </div>
 *       </form>
 *     </gp-wizard-step>
 *     <gp-wizard-step [form]="step2Form">
 *       <form  #step2Form="ngForm">
 *           <input type="text" class="form-control" required
 *             [(ngModel)]="model.age" ngControl="name" #name="ngForm">
 *           <div [attr.data-hidden]="name.valid || name.pristine" class="alert alert-danger">
 *             Age is required
 *           </div>
 *         </div>
 *       </form>
 *     </gp-wizard-step>
 *  </gp-wizard>
 * ```
 */

@Component({
  selector: 'gp-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss']
})
export class WizardComponent implements AfterContentInit {

  public isMobileDevice: boolean;

  @ContentChildren(WizardStepComponent)
  public steps: QueryList<WizardStepComponent>;

  @Output()
  public done: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public executeAdditionalAction: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public previousEvent = new EventEmitter<number>();

  @Output()
  public nextEvent = new EventEmitter<number>();

  @Input()
  public submitButtonLabel: string = 'Submit';

  @Input()
  public previousButtonLabel: string;

  @Input()
  public forceEnablePrevious: boolean = false;

  @Input()
  public disableNext: boolean = false;

  @Input()
  public readOnly = false;

  @Input()
  public displayButtons = true;

  @Input()
  public additionalLastActionButton: string;

  get currentStepIndex() {
    return this.steps.toArray().findIndex(step => step.active);
  }

  get currentStep() {
    return this.findStep(this.currentStepIndex);
  }

  get currentStepValid() {
    return this.steps.toArray().reduce((valid, step) => {
      if (valid) {
        return true;
      }
      if (step.active) {
        return step.valid;
      }
      return valid;
    }, false);
  }

  get isLastStep() {
    return this.currentStepIndex === this.steps.length - 1;
  }

  public ngAfterContentInit() {
    this.isMobileDevice = isMobileDevice();
    this.steps.forEach((step: WizardStepComponent, index: number) => step.number = index);

    if (this.steps.length > 0) {
      this.steps.first.activate();
    }
  }

  public next() {
    const index = this.currentStepIndex;
    if (!this.currentStep.valid) {
      console.warn('wizard step next() was called, though current step is not valid...');
      return;
    }
    if ((this.steps.length - 1) === index) {
      // We're done !
      this.done.emit(undefined);
    } else {
      // Validate step
      this.currentStep.completed = true;
      this.goToStep(index + 1, true);

      this.nextEvent.emit(index + 1);
    }
  }

  public onAdditionalActionClick() {
    this.executeAdditionalAction.emit(true);
  }

  public previous() {
    const previousStep = this.currentStepIndex - 1;
    this.goToStep(previousStep);

    this.previousEvent.emit(previousStep);
  }

  public goToStep(number: number, force: boolean = false) {
    if (!this.canGoToStep(number) && !force) {
      return;
    }
    for (let i = number; i < this.steps.toArray().length - 1; i++) {
      this.findStep(i).completed = false;
    }
    this.currentStep.deactivate();
    this.findStep(number).activate();
  }

  public canGoToStep(number: number) {
    const step = this.findStep(number),
      previous = this.findStep(number - 1);

    if (!isPresent(step) || step.disabled) {
      return false;
    }
    return step.number <= this.currentStepIndex || (previous && previous.completed);
  }

  public findStep(number: number) {
    return this.steps.toArray()[number];
  }

  public get percent() {
    return ((this.currentStep.number + 1) / this.steps.length) * 100 + '%';
  }

  public disablePrevious() {
    if (this.forceEnablePrevious) {
      return false;
    }
    if (this.currentStepIndex === 0) {
      return true;
    }
    return !this.canGoToStep(this.currentStepIndex - 1);
  }
}
